import React, { useMemo } from "react";
import { Pressable, ScrollView, StyleSheet, View } from "react-native";
import { ArrowsInIcon, CaretRightIcon, InfoIcon } from "@phosphor-icons/react";

import { Popover } from "../../../../../components/common/popover/Popover";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import type { ActivityIssueFinding } from "../types/reviewTypes";
import IssueFindingsList from "./IssueFindingsList";

interface ExpandedReviewContextBarProps {
  itemTypeLabel: string;
  courseName: string;
  moduleActivityThread: string;
  activityTitle: string;
  positionIndex: number;
  totalCount: number;
  whyFindings?: ActivityIssueFinding[];
  whyRubricCode?: string;
  whyRubricTitle?: string;
  whyRubricDescription?: string;
  whyLearningObjective?: string;
  onCollapse: () => void;
}

function ExpandedReviewContextBar({
  itemTypeLabel,
  courseName,
  moduleActivityThread,
  activityTitle,
  positionIndex,
  totalCount,
  whyFindings,
  whyRubricCode,
  whyRubricTitle,
  whyRubricDescription,
  whyLearningObjective,
  onCollapse,
}: ExpandedReviewContextBarProps) {
  const hasWhyFindings = Boolean(whyFindings?.length);
  const hasLearningObjective = Boolean(whyLearningObjective);
  const hasRubric = Boolean(whyRubricCode);
  const whyCount = useMemo(() => {
    let count = whyFindings?.length ?? 0;
    if (whyRubricCode) count += 1;
    if (whyLearningObjective) count += 1;
    return count;
  }, [whyFindings, whyRubricCode, whyLearningObjective]);

  const showThread = Boolean(moduleActivityThread && moduleActivityThread !== activityTitle);

  return (
    <View style={styles.bar}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Collapse expanded review"
        onPress={onCollapse}
        style={styles.collapseButton}>
        <ArrowsInIcon size={15} color={colors.grey[700]} weight="regular" />
        <CustomText text="Collapse" useTranslationText={false} size="xs" weight="medium" style={styles.collapseText} />
      </Pressable>

      <View style={styles.divider} />

      <View style={styles.typeChip}>
        <CustomText
          text={itemTypeLabel}
          useTranslationText={false}
          size="xs"
          weight="medium"
          style={styles.typeChipText}
        />
      </View>

      <View style={styles.breadcrumb}>
        <CustomText
          text={courseName}
          useTranslationText={false}
          size="xs"
          style={styles.breadcrumbMuted}
          numberOfLines={1}
        />
        <CaretRightIcon size={12} color={colors.grey[400]} weight="bold" />
        {showThread ? (
          <>
            <CustomText
              text={moduleActivityThread}
              useTranslationText={false}
              size="xs"
              style={styles.breadcrumbMuted}
              numberOfLines={1}
            />
            <CaretRightIcon size={12} color={colors.grey[400]} weight="bold" />
          </>
        ) : null}
        <CustomText
          text={activityTitle}
          useTranslationText={false}
          size="xs"
          weight="medium"
          style={styles.breadcrumbActive}
          numberOfLines={1}
        />
      </View>

      <CustomText
        text={`${positionIndex} of ${totalCount}`}
        useTranslationText={false}
        size="xs"
        style={styles.positionText}
      />

      <Popover
        triggerType="click"
        placement="bottom-end"
        maxWidth={460}
        trigger={
          <View style={styles.contextButton}>
            <InfoIcon size={14} color={colors.grey[700]} weight="regular" />
            <CustomText
              text={whyCount > 0 ? `Why this change · ${whyCount}` : "Why this change"}
              useTranslationText={false}
              size="xs"
              weight="medium"
              style={styles.contextButtonText}
            />
          </View>
        }>
        <ScrollView
          style={styles.whyScrollContainer}
          contentContainerStyle={styles.popoverSection}
          showsVerticalScrollIndicator>
          {hasWhyFindings ? <IssueFindingsList findings={whyFindings} /> : null}

          {hasLearningObjective ? (
            <View style={[styles.metaRow, hasWhyFindings && styles.metaRowWithDivider]}>
              <CustomText
                text="Learning objective: "
                useTranslationText={false}
                size="xs"
                weight="medium"
                style={styles.metaLabel}
              />
              <CustomText text={whyLearningObjective} useTranslationText={false} size="xs" style={styles.metaValue} />
            </View>
          ) : null}

          {hasRubric ? (
            <View style={[styles.metaRow, (hasWhyFindings || hasLearningObjective) && styles.metaRowWithDivider]}>
              <CustomText
                text={`${whyRubricCode}: ${whyRubricTitle ?? ""}`.trim()}
                useTranslationText={false}
                size="xs"
                weight="medium"
                style={styles.rubricTitle}
              />
              {whyRubricDescription ? (
                <CustomText text={whyRubricDescription} useTranslationText={false} size="xs" style={styles.metaValue} />
              ) : null}
            </View>
          ) : null}

          {!hasWhyFindings && !hasLearningObjective && !hasRubric ? (
            <CustomText
              text="No recommendation rationale is available for this item yet."
              useTranslationText={false}
              size="xs"
              style={styles.metaValue}
            />
          ) : null}
        </ScrollView>
      </Popover>
    </View>
  );
}

const styles = StyleSheet.create({
  bar: {
    minHeight: 40,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    backgroundColor: colors.white,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  collapseButton: {
    height: 28,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
    paddingHorizontal: 10,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  collapseText: {
    color: colors.grey[700],
  },
  divider: {
    width: 1,
    height: 18,
    backgroundColor: colors.grey[200],
  },
  typeChip: {
    paddingHorizontal: 8,
    height: 22,
    borderRadius: 999,
    backgroundColor: colors.grey[100],
    alignItems: "center",
    justifyContent: "center",
  },
  typeChipText: {
    color: colors.grey[600],
  },
  breadcrumb: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  breadcrumbMuted: {
    color: colors.grey[500],
    flexShrink: 1,
  },
  breadcrumbActive: {
    color: colors.grey[900],
    flexShrink: 1,
  },
  positionText: {
    color: colors.grey[500],
    fontVariant: ["tabular-nums"],
  },
  contextButton: {
    height: 28,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
    paddingHorizontal: 10,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  contextButtonText: {
    color: colors.grey[700],
  },
  whyScrollContainer: {
    maxHeight: 320,
  },
  popoverSection: {
    gap: 10,
  },
  metaRow: {
    gap: 4,
    paddingTop: 8,
  },
  metaRowWithDivider: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[100],
  },
  metaLabel: {
    color: colors.grey[700],
  },
  metaValue: {
    color: colors.grey[600],
  },
  rubricTitle: {
    color: colors.brand[600],
  },
});

export default ExpandedReviewContextBar;
